import { gsap } from 'gsap';
import { ScrollTrigger } from 'gsap/ScrollTrigger';
import { SplitText } from 'gsap/SplitText';
import { whenPageRevealed } from '@/js/lib/page-reveal.js';

gsap.registerPlugin( ScrollTrigger, SplitText );

const defaults = {
	direction:     'right',
	scrollStart:   'top 90%',
	staggerStart:  'start',
	stagger:       100,
	barDuration:   0.6,
	barEase:       'power3.inOut',
};

const directionMap = {
	right: { prop: 'scaleX', origin: 'right center' },
	left:  { prop: 'scaleX', origin: 'left center' },
	up:    { prop: 'scaleY', origin: 'center top' },
	down:  { prop: 'scaleY', origin: 'center bottom' },
};

function createBar( color, origin ) {
	const bar = document.createElement( 'div' );
	bar.className = 'highlight-marker-bar';
	Object.assign( bar.style, {
		backgroundColor: color,
		transformOrigin: origin,
	} );
	return bar;
}

function cleanupElement( el ) {
	if ( ! el._highlightMarkerReveal ) {
		return;
	}
	el._highlightMarkerReveal.timeline?.kill();
	el._highlightMarkerReveal.scrollTrigger?.kill();
	el._highlightMarkerReveal.split?.revert();
	el.querySelectorAll( '.highlight-marker-bar' ).forEach( ( bar ) => bar.remove() );
	delete el._highlightMarkerReveal;
}

// Selecteert h1/h2 in main content. Slaat sr-only en expliciete opt-out over.
function collectTargets() {
	const all = document.querySelectorAll( 'h1, h2' );
	const targets = [];
	all.forEach( ( el ) => {
		if ( el.closest( '.sr-only' ) || el.classList.contains( 'sr-only' ) ) {
			return;
		}
		if ( el.getAttribute( 'data-highlight-marker-reveal' ) === 'false' ) {
			return;
		}
		if ( ! el.hasAttribute( 'data-highlight-marker-reveal' ) ) {
			el.setAttribute( 'data-highlight-marker-reveal', '' );
		}
		targets.push( el );
	} );
	return targets;
}

function initHighlightMarkerTextReveal() {
	const reduceMotion = window.matchMedia( '(prefers-reduced-motion: reduce)' ).matches;

	const elements = collectTargets();
	if ( ! elements.length ) {
		return;
	}

	if ( reduceMotion ) {
		elements.forEach( ( el ) => {
			gsap.set( el, { autoAlpha: 1 } );
		} );
		return;
	}

	elements.forEach( cleanupElement );

	elements.forEach( ( el ) => {
		const direction     = el.getAttribute( 'data-marker-direction' )     || defaults.direction;
		const scrollStart   = el.getAttribute( 'data-marker-scroll-start' )  || defaults.scrollStart;
		const staggerStart  = el.getAttribute( 'data-marker-stagger-start' ) || defaults.staggerStart;
		const staggerOffset = ( parseFloat( el.getAttribute( 'data-marker-stagger' ) ) || defaults.stagger ) / 1000;

		const dirConfig = directionMap[ direction ] || directionMap.right;

		el._highlightMarkerReveal = {};

		const split = SplitText.create( el, {
			type:        'lines',
			linesClass:  'highlight-marker-line',
			autoSplit:   true,
			onSplit( self ) {
				const instance = el._highlightMarkerReveal;

				instance.timeline?.kill();
				instance.scrollTrigger?.kill();
				el.querySelectorAll( '.highlight-marker-bar' ).forEach( ( bar ) => bar.remove() );

				const lines = self.lines;
				const tl    = gsap.timeline( { paused: true } );
				// De balk heeft altijd de kleur van de tekst eronder. Bij het
				// starten opnieuw meten: de kleur kan intussen meegekleurd zijn.
				const color = getComputedStyle( el ).color;
				const bars  = [];

				lines.forEach( ( line, i ) => {
					gsap.set( line, { position: 'relative', overflow: 'hidden' } );

					const bar = createBar( color, dirConfig.origin );
					line.appendChild( bar );
					bars.push( bar );

					const staggerIndex = staggerStart === 'end' ? lines.length - 1 - i : i;

					tl.to( bar, {
						[ dirConfig.prop ]: 0,
						duration:           defaults.barDuration,
						ease:               defaults.barEase,
					}, staggerIndex * staggerOffset );
				} );

				gsap.set( el, { autoAlpha: 1 } );

				const st = ScrollTrigger.create( {
					trigger: el,
					start:   scrollStart,
					once:    true,
					onEnter: () => {
						const current = getComputedStyle( el ).color;
						bars.forEach( ( bar ) => {
							bar.style.backgroundColor = current;
						} );
						tl.play();
					},
				} );

				instance.timeline      = tl;
				instance.scrollTrigger = st;
			},
		} );

		el._highlightMarkerReveal.split = split;
	} );
}

function bootstrap() {
	Promise.all( [ document.fonts?.ready, whenPageRevealed() ] ).then( initHighlightMarkerTextReveal );
}

if ( document.readyState === 'loading' ) {
	document.addEventListener( 'DOMContentLoaded', bootstrap );
} else {
	bootstrap();
}

export { initHighlightMarkerTextReveal };
